import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Vault, Sparkles, FileText, Type, BookOpen, Dna } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tier colors
const TIER_COLORS = {
  1: 'from-stone-400 to-stone-500',
  2: 'from-amber-600 to-amber-700',
  3: 'from-amber-500 to-yellow-500',
  4: 'from-yellow-400 to-amber-400',
  5: 'from-yellow-300 to-amber-300',
};

const TIER_GLOW = {
  1: '',
  2: 'shadow-amber-500/20',
  3: 'shadow-amber-400/30',
  4: 'shadow-yellow-400/40',
  5: 'shadow-yellow-300/50 animate-pulse',
};

const ICON_MAP = {
  dna: Dna,
  book: BookOpen,
  'file-text': FileText,
  type: Type,
};

/**
 * Cofre de Identidade Literária
 * Visual representation of author's accumulated creative assets
 */
const CofreIdentidade = ({ compact = false }) => {
  const [vault, setVault] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVault();
  }, []);

  const loadVault = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/creative/vault`, {
        withCredentials: true
      });
      setVault(response.data);
    } catch (error) {
      console.error('Error loading vault:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`animate-pulse ${compact ? 'h-16' : 'h-40'} bg-muted rounded-xl`} />
    );
  }

  if (!vault) return null;

  const tierColor = TIER_COLORS[vault.vault_tier] || TIER_COLORS[1];
  const tierGlow = TIER_GLOW[vault.vault_tier] || '';

  if (compact) {
    return (
      <div className={`flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r ${tierColor} text-white ${tierGlow}`}>
        <Vault size={20} />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium opacity-90">{vault.vault_level}</p>
          <p className="text-sm font-bold">{vault.total_aks.toLocaleString()} AKs</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 rounded-xl border border-amber-200/50 dark:border-amber-800/30 bg-gradient-to-br from-amber-50/80 to-orange-50/80 dark:from-amber-950/20 dark:to-orange-950/20 ${tierGlow}`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tierColor} flex items-center justify-center shadow-lg`}>
          <Vault size={24} className="text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">Cofre de Identidade</h3>
          <p className="text-sm text-amber-700 dark:text-amber-300">{vault.vault_level}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
            {vault.total_aks.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">AKs acumulados</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span>Progresso</span>
          <span>{vault.progress_percent.toFixed(0)}%</span>
        </div>
        <div className="h-2 bg-amber-200/50 dark:bg-amber-900/30 rounded-full overflow-hidden">
          <div 
            className={`h-full bg-gradient-to-r ${tierColor} transition-all duration-500`}
            style={{ width: `${vault.progress_percent}%` }}
          />
        </div>
        {vault.next_threshold && (
          <p className="text-xs text-muted-foreground mt-1">
            Próximo nível: {vault.next_threshold.toLocaleString()} AKs
          </p>
        )}
      </div>

      {/* Vault Items */}
      {vault.vault_items && vault.vault_items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {vault.vault_items.map((item, index) => {
            const IconComponent = ICON_MAP[item.icon] || Sparkles;
            return (
              <div 
                key={index}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/60 dark:bg-gray-800/60 text-xs"
              >
                <IconComponent size={12} className="text-amber-600 dark:text-amber-400" />
                <span>{item.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CofreIdentidade;
